import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  Search,
  Moon,
  Sun,
  Settings,
  LogOut,
  User,
  ChevronDown,
  Palette,
  Crown,
  Building2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useThemeStore, SIDEBAR_COLLAPSED_WIDTH } from '../../stores/theme.store';
import { useAuthStore } from '../../stores/auth.store';
import { Avatar } from '../ui/Avatar';
import { themes } from '../../config/theme';

export function Header() {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode, currentTheme, setTheme, sidebarCollapsed, sidebarWidth } = useThemeStore();
  const { user, logout, isSuperAdmin } = useAuthStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  // Calculate actual left position based on collapsed state
  const headerLeft = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : sidebarWidth;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Tenant Context Indicator Component
  const TenantIndicator = () => {
    if (isSuperAdmin()) {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-lg">
          <Crown className="w-4 h-4 text-yellow-400" />
          <span className="text-sm font-medium text-yellow-400">Super Admin</span>
        </div>
      );
    }
    
    if (user?.tenantName) {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-500/10 border border-primary-500/30 rounded-lg">
          <Building2 className="w-4 h-4 text-primary-400" />
          <span className="text-sm font-medium text-primary-400 max-w-[200px] truncate" title={user.tenantName}>
            {user.tenantName}
          </span>
        </div>
      );
    }
    
    return null;
  };

  return (
    <header
      className="fixed top-0 right-0 h-16 bg-surface-950/80 backdrop-blur-xl border-b border-surface-800 z-30 flex items-center justify-between px-6 transition-all"
      style={{ left: `${headerLeft}px` }}
    >
      {/* Search */}
      <div className="flex-1 max-w-xl">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
          <input
            type="text"
            placeholder="Buscar transações, contas, usuários..."
            className="w-full pl-10 pr-4 py-2 bg-surface-900/50 border border-surface-800 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 bg-surface-800 rounded text-xs text-surface-500">
            ⌘K
          </kbd>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {/* Tenant Context Indicator */}
        <div className="hidden md:block mr-2">
          <TenantIndicator />
        </div>

        {/* Theme Selector */}
        <div className="relative">
          <button
            onClick={() => setShowThemeMenu(!showThemeMenu)}
            className="p-2 rounded-lg text-surface-400 hover:text-surface-100 hover:bg-surface-800 transition-colors"
          >
            <Palette className="w-5 h-5" />
          </button>
          <AnimatePresence>
            {showThemeMenu && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="dropdown"
              >
                <div className="p-2">
                  <p className="px-3 py-2 text-xs font-medium text-surface-500 uppercase">
                    Temas
                  </p>
                  {Object.entries(themes).map(([key, theme]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setTheme(key);
                        setShowThemeMenu(false);
                      }}
                      className={cn(
                        'w-full dropdown-item rounded-lg',
                        currentTheme === key && 'bg-primary-500/10 text-primary-400'
                      )}
                    >
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{
                          background: `rgb(${theme.colors.primary[500]})`,
                        }}
                      />
                      {theme.name}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Dark Mode Toggle */}
        <button
          onClick={toggleDarkMode}
          className="p-2 rounded-lg text-surface-400 hover:text-surface-100 hover:bg-surface-800 transition-colors"
        >
          {isDarkMode ? (
            <Sun className="w-5 h-5" />
          ) : (
            <Moon className="w-5 h-5" />
          )}
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 rounded-lg text-surface-400 hover:text-surface-100 hover:bg-surface-800 transition-colors"
          >
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-primary-500 rounded-full" />
          </button>
          <AnimatePresence>
            {showNotifications && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="dropdown w-80"
              >
                <div className="p-4 border-b border-surface-800">
                  <h3 className="font-semibold text-surface-100">Notificações</h3>
                </div>
                <div className="p-4 text-center text-surface-500 text-sm">
                  Nenhuma notificação
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* User Menu */}
        <div className="relative ml-2">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-3 p-1.5 rounded-lg hover:bg-surface-800 transition-colors"
          >
            <Avatar name={user?.name} size="sm" status="online" />
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium text-surface-100">
                {user?.name || 'Usuário'}
              </p>
              <p className="text-xs text-surface-500">
                {user?.type || 'Admin'}
              </p>
            </div>
            <ChevronDown className="w-4 h-4 text-surface-500" />
          </button>
          <AnimatePresence>
            {showUserMenu && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="dropdown"
              >
                <div className="p-2">
                  <button className="w-full dropdown-item rounded-lg">
                    <User className="w-4 h-4" />
                    Meu Perfil
                  </button>
                  <button
                    onClick={() => navigate('/settings')}
                    className="w-full dropdown-item rounded-lg"
                  >
                    <Settings className="w-4 h-4" />
                    Configurações
                  </button>
                  <div className="my-2 border-t border-surface-800" />
                  <button
                    onClick={handleLogout}
                    className="w-full dropdown-item rounded-lg text-red-400 hover:text-red-300"
                  >
                    <LogOut className="w-4 h-4" />
                    Sair
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
}
